import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Tela } from "@/components/casca/CabecalhoPagina";
import { CabecalhoResultados, Kpi } from "@/components/relatorio/Resultados";
import { Vazio } from "@/components/casca/Vazio";
import { Icone } from "@/components/Icone";
import { comoChamar } from "@/lib/painel/telefone";
import { funilQuery } from "../consultas";

/** A cor da marca, um pouco mais clara a cada etapa: a barra esmaece conforme afunila. */
const CORES = ["opacity-100", "opacity-90", "opacity-80", "opacity-70", "opacity-60", "opacity-50"];

/** Quantas conversas entraram e até onde cada uma chegou — em barras ou em tabela. */
export function Funil({ verTabela }: { verTabela: boolean }) {
  const { data: d } = useSuspenseQuery(funilQuery());

  const topo = d.etapas[0]?.alcancaram ?? 0;
  const acha = (chave: string) => d.etapas.find((e) => e.id === chave || e.rotulo.toLowerCase().includes(chave))?.alcancaram ?? 0;
  const qualificadas = acha("qualificad");
  const agendaram = acha("agend");
  const paradas = d.etapas.reduce((acc, e) => acc + e.pararamAqui, 0);
  const perc = (n: number) => (topo > 0 ? Math.round((n / topo) * 100) : 0);

  return (
    <Tela>
      <CabecalhoResultados
        acao={
          topo > 0 ? (
            <Link
              to="/funil"
              search={verTabela ? {} : { ver: "tabela" }}
              className="flex h-9 items-center gap-1.5 rounded-full bg-[var(--color-cartao)] px-4 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao hover:bg-[var(--color-painel)]"
            >
              {verTabela ? "Ver em barras" : "Ver em tabela"}
            </Link>
          ) : undefined
        }
      />

      {topo === 0 ? (
        <Vazio icone="funil" titulo="O funil ainda está vazio">
          As etapas se preenchem sozinhas conforme as conversas avançam: chegou, qualificou, agendou, compareceu.
        </Vazio>
      ) : (
        <div className="flex flex-col gap-5">
          <p className="-mt-2 max-w-2xl text-[13px] text-[var(--color-fraco)]">
            Quantas conversas entraram e até onde cada uma chegou. Cada número vem de um lugar: as conversas do banco, o interesse e o “agendou?”
            da ficha, os horários da agenda.
          </p>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(170px,100%),1fr))] gap-3">
            <Kpi rotulo="Conversas" valor={d.conversas} nota="no período" />
            <Kpi rotulo="Qualificadas" valor={qualificadas} nota={`${perc(qualificadas)}% de quem entrou`} />
            <Kpi rotulo="Agendaram" valor={agendaram} nota={`${perc(agendaram)}% de quem entrou`} />
            <Kpi rotulo="Paradas" valor={paradas} nota="o assistente falou e ninguém voltou" />
          </div>

          {!verTabela ? (
            <div className="rounded-[14px] bg-[var(--color-cartao)] px-5 py-1 shadow-cartao">
              {d.etapas.map((e, idx) => {
                const anterior = d.etapas[idx - 1]?.alcancaram ?? 0;
                const percAnterior = idx > 0 && anterior > 0 ? Math.round((e.alcancaram / anterior) * 100) : null;
                const largura = topo > 0 ? Math.min(100, Math.max(2, (e.alcancaram / topo) * 100)) : 0;
                return (
                  <div
                    key={e.id}
                    className="grid grid-cols-[1fr_64px] items-center gap-x-4 gap-y-2 border-b border-[var(--color-borda)] py-3.5 last:border-0 md:grid-cols-[170px_1fr_72px]"
                  >
                    <div>
                      <p className="text-[15px] font-bold">{e.rotulo}</p>
                      <p className="text-[12.5px] text-[var(--color-fraco)]">{percAnterior === null ? "topo do funil" : `${percAnterior}% da etapa anterior`}</p>
                    </div>
                    <div className="col-span-2 row-start-2 h-7 overflow-hidden rounded-lg bg-[var(--color-painel)] md:col-span-1 md:row-start-auto">
                      <div className={`h-full rounded-lg bg-[var(--color-marca)] transition-all duration-500 ${CORES[idx % CORES.length]}`} style={{ width: `${largura}%` }} />
                    </div>
                    <p className="num col-start-2 row-start-1 text-right text-lg font-bold md:col-start-auto md:row-start-auto">{e.alcancaram}</p>
                    {e.pararamAqui > 0 && (
                      <p className="col-span-2 text-[12.5px] text-[var(--color-fraco)] md:col-span-2 md:col-start-2">
                        <b className="font-semibold text-[var(--color-suave)]">
                          {e.pararamAqui} {e.pararamAqui === 1 ? "parou" : "pararam"} aqui
                        </b>
                        {e.quemParou.length > 0 && (
                          <>
                            {" — "}
                            {e.quemParou.map((p, j) => (
                              <span key={p.telefone}>
                                {j > 0 ? ", " : ""}
                                <Link to="/conversas/$telefone" params={{ telefone: p.telefone }} className="font-semibold text-[var(--color-marca-forte)] hover:underline">
                                  {comoChamar(p.nome, p.telefone)}
                                </Link>
                              </span>
                            ))}
                            {e.pararamAqui > e.quemParou.length ? "…" : ""}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-[14px] bg-[var(--color-cartao)] shadow-cartao">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead className="text-xs font-bold tracking-[0.05em] text-[var(--color-fraco)] uppercase">
                  <tr className="border-b border-[var(--color-borda)]">
                    <th className="px-4 py-3">Etapa</th>
                    <th className="px-4 py-3">Alcançaram</th>
                    <th className="px-4 py-3">% do topo</th>
                    <th className="px-4 py-3">Pararam</th>
                  </tr>
                </thead>
                <tbody>
                  {d.etapas.map((e) => (
                    <tr key={e.id} className="border-b border-[var(--color-borda)] last:border-0 hover:bg-[var(--color-painel)]">
                      <td className="px-4 py-3 font-semibold">{e.rotulo}</td>
                      <td className="px-4 py-3 font-bold">{e.alcancaram}</td>
                      <td className="px-4 py-3 text-[var(--color-suave)]">{e.percentualDoTopo}%</td>
                      <td className="px-4 py-3 text-[var(--color-suave)]">
                        {e.pararamAqui > 0 ? `${e.pararamAqui} ${e.pararamAqui === 1 ? "pessoa" : "pessoas"}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {d.avisos.map((a, i) => (
              <p key={i} className="flex items-start gap-2.5 rounded-[14px] bg-[var(--color-atencao-fundo)] px-4 py-3 text-[13px] leading-relaxed text-[var(--color-atencao)]">
                <Icone nome="alerta" className="mt-0.5 h-4 w-4 shrink-0" />
                {a}
              </p>
            ))}
            <p className="flex items-start gap-2.5 px-1 text-[13px] leading-relaxed text-[var(--color-fraco)]">
              <Icone nome="info" className="mt-0.5 h-4 w-4 shrink-0" />
              “Horário já passou” não é comparecimento. Nada no sistema registra se a pessoa realmente foi à clínica — só que o horário foi marcado, a
              data passou, e alguém da equipe confirmou na Agenda.
            </p>
          </div>
        </div>
      )}
    </Tela>
  );
}
